import React, { useState, useEffect, useCallback } from 'react';
import { 
  Box, 
  IconButton,
  Tooltip, 
  CircularProgress,
  Snackbar,
  Alert,
  useMediaQuery,
  useTheme,
  Badge,
  Chip
} from '@mui/material';
import {
  Notifications,
  NotificationsOff,
  Settings,
  Email,
  Phone
} from '@mui/icons-material';
import NotificationSettingsDialog from './NotificationSettingsDialog';
import authService from '../services/authService';
import api from '../services/api';

const NotificationToggle = () => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [settings, setSettings] = useState({
    pushNotifications: false,
    emailNotifications: false
  });
  const [loading, setLoading] = useState(true);
  const [isSupported, setIsSupported] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Load notification settings
  const loadNotificationSettings = useCallback(async () => {
    try {
      const response = await api.get('/notifications/settings');
      
      // Axios puts data in response.data
      const data = response.data;
      
      setSettings({
        pushNotifications: Boolean(data.push_notifications),
        emailNotifications: Boolean(data.email_notifications)
      });
    } catch (error) {
      console.error('Failed to load notification settings:', error);
      // On error, keep defaults (both false)
      setSettings({
        pushNotifications: false,
        emailNotifications: false
      });
    }
  }, []);

  // Check if user is authenticated and notifications are supported
  useEffect(() => {
    const checkNotificationStatus = async () => {
      // Check if notifications are supported
      const supported = ('Notification' in window) && 
                        ('serviceWorker' in navigator) && 
                        ('PushManager' in window);
      setIsSupported(supported);
      
      // Check if user is authenticated
      const isAuthenticated = authService.isAuthenticated();
      if (!isAuthenticated) {
        setLoading(false);
        return;
      }
      
      // Load current notification settings
      await loadNotificationSettings();
      setLoading(false);
    };
    
    checkNotificationStatus();
  }, [loadNotificationSettings]);

  const handleSettingsChange = (newSettings) => {
    setSettings(newSettings);
    
    // Show success message
    const enabledMethods = [];
    if (newSettings.pushNotifications) enabledMethods.push('Push');
    if (newSettings.emailNotifications) enabledMethods.push('Email');
    
    let message;
    if (enabledMethods.length === 2) {
      message = 'Push + Email notifications enabled';
    } else if (enabledMethods.length === 1) {
      message = `${enabledMethods[0]} notifications enabled`;
    } else {
      message = 'Notifications disabled - check dashboard manually';
    }
    
    setSnackbar({
      open: true,
      message,
      severity: enabledMethods.length > 0 ? 'success' : 'warning'
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleOpenDialog = () => {
    if (!authService.isAuthenticated()) {
      setSnackbar({
        open: true,
        message: 'Please log in to configure notifications',
        severity: 'warning'
      });
      return;
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  // Show disabled state if not supported (but only for push - email always works)
  if (!isSupported) {
    // Still allow opening dialog for email notifications even if push not supported
  }

  const hasAnyNotifications = settings.pushNotifications || settings.emailNotifications;
  const notificationCount = (settings.pushNotifications ? 1 : 0) + (settings.emailNotifications ? 1 : 0);

  const getStatusInfo = () => {
    const both = settings.pushNotifications && settings.emailNotifications;
    const pushOnly = settings.pushNotifications && !settings.emailNotifications;
    const emailOnly = !settings.pushNotifications && settings.emailNotifications;

    if (both) return { icon: <Notifications />, color: '#10b981', text: 'Push + Email' };
    if (pushOnly) return { icon: <Phone />, color: '#3b82f6', text: 'Push Only' };
    if (emailOnly) return { icon: <Email />, color: '#8b5cf6', text: 'Email Only' };
    return { icon: <NotificationsOff />, color: '#6b7280', text: 'Off' };
  };

  const statusInfo = getStatusInfo();

  // Mobile version - compact with better status indicators
  if (isMobile) {
    return (
      <>
        <Tooltip title={`Notifications: ${statusInfo.text}`}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center',
            borderRadius: '12px',
            background: hasAnyNotifications 
              ? 'rgba(191, 155, 48, 0.05)' 
              : 'rgba(107, 114, 128, 0.05)',
            border: hasAnyNotifications
              ? '1px solid rgba(191, 155, 48, 0.2)'
              : '1px solid rgba(107, 114, 128, 0.1)',
            p: 0.5,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              background: hasAnyNotifications
                ? 'rgba(191, 155, 48, 0.1)'
                : 'rgba(107, 114, 128, 0.1)',
              transform: 'scale(1.05)',
              boxShadow: hasAnyNotifications
                ? '0 4px 12px rgba(191, 155, 48, 0.3)'
                : '0 4px 12px rgba(107, 114, 128, 0.2)'
            }
          }}>
            <IconButton
              onClick={handleOpenDialog}
              size="small"
              disabled={loading}
              sx={{ 
                p: 0.5,
                '&:hover': { backgroundColor: 'transparent' }
              }}
            >
              {loading ? (
                <CircularProgress size={16} sx={{ color: '#bf9b30' }} />
              ) : (
                <Box sx={{ 
                  color: statusInfo.color,
                  filter: hasAnyNotifications 
                    ? 'drop-shadow(0 0 4px rgba(191, 155, 48, 0.5))' 
                    : 'none',
                  fontSize: '1.2rem',
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  {statusInfo.icon}
                </Box>
              )}
            </IconButton>
          </Box>
        </Tooltip>
        
        <NotificationSettingsDialog
          open={dialogOpen}
          onClose={handleCloseDialog}
          onSettingsChange={handleSettingsChange}
        />
        
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert 
            onClose={handleCloseSnackbar} 
            severity={snackbar.severity}
            sx={{ borderRadius: 2 }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </>
    );
  }

  // Desktop version - with detailed display
  return (
    <>
      <Tooltip title="Configure Notification Settings">
        <Box
          onClick={handleOpenDialog}
          sx={{ 
            display: 'flex', 
            alignItems: 'center',
            gap: 1,
            cursor: loading ? 'wait' : 'pointer',
            mr: 2,
            borderRadius: '12px',
            background: 'rgba(191, 155, 48, 0.05)',
            border: '1px solid rgba(191, 155, 48, 0.1)',
            px: 2,
            py: 1,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              background: 'rgba(191, 155, 48, 0.1)',
              transform: 'translateY(-2px)',
              boxShadow: '0 4px 12px rgba(191, 155, 48, 0.3)'
            }
          }}
        >
          {loading ? (
            <CircularProgress size={20} sx={{ color: '#bf9b30' }} />
          ) : (
            <>
              <Badge 
                badgeContent={notificationCount} 
                color="success"
                variant={notificationCount > 0 ? "standard" : "dot"}
                invisible={!hasAnyNotifications}
              >
                {hasAnyNotifications ? (
                  <Notifications 
                    sx={{ 
                      color: '#bf9b30',
                      filter: 'drop-shadow(0 0 4px rgba(191, 155, 48, 0.5))'
                    }} 
                  />
                ) : (
                  <NotificationsOff color="disabled" />
                )}
              </Badge>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <span style={{ fontSize: '0.9rem', fontWeight: '600' }}>Notifications</span>
                  <Settings sx={{ fontSize: 14, opacity: 0.7 }} />
                </Box>
                
                {hasAnyNotifications ? (
                  <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                    {settings.pushNotifications && (
                      <Chip
                        icon={<Phone sx={{ fontSize: 12 }} />}
                        label="Push"
                        size="small"
                        variant="outlined"
                        sx={{ 
                          fontSize: '0.7rem', 
                          height: 20,
                          borderColor: '#bf9b30',
                          color: '#bf9b30',
                          '& .MuiChip-icon': {
                            color: '#bf9b30'
                          }
                        }}
                      />
                    )}
                    {settings.emailNotifications && (
                      <Chip
                        icon={<Email sx={{ fontSize: 12 }} />}
                        label="Email"
                        size="small"
                        variant="outlined"
                        sx={{ 
                          fontSize: '0.7rem', 
                          height: 20,
                          borderColor: '#bf9b30',
                          color: '#bf9b30',
                          '& .MuiChip-icon': {
                            color: '#bf9b30'
                          }
                        }}
                      />
                    )}
                  </Box>
                ) : (
                  <span style={{ fontSize: '0.7rem', opacity: 0.7 }}>Click to configure</span>
                )}
              </Box>
            </>
          )}
        </Box>
      </Tooltip>
      
      <NotificationSettingsDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        onSettingsChange={handleSettingsChange}
      />
      
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default NotificationToggle;